import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { timekeepingApi } from "../../services/timekeepingApi";
import { usePermissions } from "../../hooks/usePermissions";
import { StatusBadge } from "../../components/StatusBadge";
import { CheckCircle2, Lock, Undo2, AlertOctagon } from "lucide-react";
import { toast } from "sonner";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { ScanViewer } from "../../components/timekeeping/ScanViewer";
import { DailyRecordRow } from "../../components/timekeeping/DailyRecordRow";
import { CorrectionDialog } from "../../components/timekeeping/CorrectionDialog";
import { ReopenMonthDialog } from "../../components/timekeeping/ReopenMonthDialog";
import { LineageIndicator } from "../../components/timekeeping/LineageIndicator";
import { useLocalization } from "../../hooks/useLocalization";

function TableHeader() {
  const { t } = useLocalization();
  return (
    <thead className="bg-gray-50 text-gray-500 text-left sticky top-0">
      <tr>
        <th className="px-3 py-2 font-medium">{t("common.date")}</th>
        <th className="px-3 py-2 font-medium">{t("mgmtTimekeeping.colScheduled")}</th>
        <th className="px-3 py-2 font-medium">{t("mgmtTimekeeping.colExtracted")}</th>
        <th className="px-3 py-2 font-medium">{t("mgmtTimekeeping.colCorrected")}</th>
        <th className="px-3 py-2 font-medium">{t("mgmtTimekeeping.colDifference")}</th>
        <th className="px-3 py-2 font-medium">{t("common.status")}</th>
        <th className="px-3 py-2 font-medium">{t("mgmtTimekeeping.colAction")}</th>
      </tr>
    </thead>
  );
}

export default function TimekeepingReviewPage() {
  const { employeeId } = useParams();
  const [searchParams] = useSearchParams();
  const payrollMonth = searchParams.get("payroll_month") || "";
  const { t } = useLocalization();
  const { canReviewTimecards, canLockPayrollTimecards } = usePermissions();
  const [review, setReview] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [correctingRow, setCorrectingRow] = useState(null);
  const [reopenOpen, setReopenOpen] = useState(false);

  const load = () => {
    if (!payrollMonth) return;
    setLoadError(null);
    timekeepingApi.getEmployeeMonthReview(employeeId, payrollMonth)
      .then((r) => setReview(r.data))
      .catch((e) => { setReview(null); setLoadError(e?.response?.data?.detail || t("mgmtTimekeeping.couldNotLoadReview")); });
  };

  useEffect(() => { load(); }, [employeeId, payrollMonth]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!canReviewTimecards) return <p className="text-sm text-gray-500" data-testid="timekeeping-review-access-denied">{t("common.accessDenied")}</p>;
  if (!payrollMonth) return <p className="text-sm text-gray-500">{t("mgmtTimekeeping.missingPayrollMonthParam")}</p>;
  if (loadError) return <p className="text-sm text-red-500" data-testid="timekeeping-review-load-error">{loadError} - {t("mgmtTimekeeping.pairNotLinkedNotice")}</p>;
  if (!review) return <p className="text-sm text-gray-500" data-testid="timekeeping-review-loading">{t("mgmtTimekeeping.loadingReview")}</p>;

  const submitCorrection = async (date, form) => {
    try {
      await timekeepingApi.correctDay(employeeId, {
        date, correction_type: form.correction_type,
        new_clock_in: form.new_clock_in || null, new_clock_out: form.new_clock_out || null, reason: form.reason,
      });
      toast.success(t("mgmtTimekeeping.correctionSaved"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("mgmtTimekeeping.correctionFailed"));
      throw e;
    }
  };

  const approveMonth = async () => {
    try {
      await timekeepingApi.approveMonth(employeeId, payrollMonth);
      toast.success(t("mgmtTimekeeping.monthApproved"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("mgmtTimekeeping.approveFailed"));
    }
  };

  const lockMonth = async () => {
    try {
      await timekeepingApi.lockMonth(employeeId, payrollMonth);
      toast.success(t("mgmtTimekeeping.lockedForPayroll"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("mgmtTimekeeping.lockFailed"));
    }
  };

  const reopenMonth = async (reason) => {
    try {
      await timekeepingApi.reopenMonth(employeeId, payrollMonth, reason);
      toast.success(t("mgmtTimekeeping.monthReopened"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("mgmtTimekeeping.reopenFailed"));
      throw e;
    }
  };

  const isLocked = review.official_record_status === "LOCKED_FOR_PAYROLL";
  const exceptionCount = review.daily_records.filter((r) => r.classification !== "MATCH").length;

  const headerBar = (
    <div className="flex items-center justify-between flex-wrap gap-3 shrink-0">
      <div>
        <h2 className="text-xl font-bold tracking-tight text-gray-900">{review.employee_name}</h2>
        <p className="text-xs text-gray-500">{review.employee_code} - {t("mgmtTimekeeping.payrollMonthLabel")} {payrollMonth}</p>
      </div>
      <div className="flex items-center gap-2">
        <StatusBadge status={review.official_record_status} />
        {!isLocked && (
          <button data-testid="approve-month-btn" onClick={approveMonth} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-green-600 text-white hover:bg-green-700">
            <CheckCircle2 size={14} /> {t("mgmtTimekeeping.approveMonth")}
          </button>
        )}
        {!isLocked && review.official_record_status === "APPROVED" && canLockPayrollTimecards && (
          <button data-testid="lock-month-btn" onClick={lockMonth} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-gray-900 text-white hover:bg-gray-700">
            <Lock size={14} /> {t("mgmtTimekeeping.lockForPayroll")}
          </button>
        )}
        {isLocked && canLockPayrollTimecards && (
          <button data-testid="reopen-month-btn" onClick={() => setReopenOpen(true)} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold border border-gray-200 hover:bg-gray-50">
            <Undo2 size={14} /> {t("mgmtTimekeeping.reopen")}
          </button>
        )}
      </div>
    </div>
  );

  const dailyTable = (
    <div className="bg-white rounded-xl border border-gray-200 overflow-auto flex-1" data-testid="daily-records-table-wrap">
      <table className="w-full text-sm min-w-[640px]">
        <TableHeader />
        <tbody>
          {review.daily_records.map((row) => (
            <DailyRecordRow key={row.date} row={row} review={review} isLocked={isLocked} onCorrect={setCorrectingRow} />
          ))}
          {review.daily_records.length === 0 && (
            <tr><td colSpan={7} className="px-3 py-6 text-center text-gray-500" data-testid="no-daily-records-row">{t("mgmtTimekeeping.noDailyRecords")}</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="flex flex-col gap-4" data-testid="timekeeping-review-page">
      <PageHelpBanner pageKey="timekeeping-review" title={t("pageHelp.defaultTitle")}>
        <p dangerouslySetInnerHTML={{ __html: t("mgmtTimekeeping.reviewHelp") }} />
      </PageHelpBanner>

      {headerBar}

      {exceptionCount > 0 && (
        <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 w-fit" data-testid="exception-count-banner">
          <AlertOctagon size={13} /> {exceptionCount} {exceptionCount > 1 ? t("mgmtTimekeeping.daysWithException") : t("mgmtTimekeeping.dayWithException")}
        </div>
      )}

      {/* Desktop: true left/right split, ~55/45 */}
      <div className="hidden lg:flex gap-4 h-[calc(100vh-260px)] min-h-[520px]">
        <div className="w-[55%] shrink-0 grid grid-cols-1 xl:grid-cols-2 gap-2 bg-white rounded-xl border border-gray-200 p-3">
          <ScanViewer file={review.blue_file} label="BLUE" />
          <ScanViewer file={review.red_file} label="RED" />
        </div>
        <div className="flex-1 flex flex-col min-w-0 overflow-y-auto">{dailyTable}</div>
      </div>

      {/* Mobile: tabs, never side-by-side source + table on a narrow screen */}
      <div className="lg:hidden">
        <Tabs defaultValue="source" data-testid="mobile-timekeeping-tabs">
          <TabsList className="grid grid-cols-4 w-full">
            <TabsTrigger data-testid="mobile-tab-source" value="source">{t("mgmtTimekeeping.tabSource")}</TabsTrigger>
            <TabsTrigger data-testid="mobile-tab-attendance" value="attendance">{t("mgmtTimekeeping.tabAttendance")}</TabsTrigger>
            <TabsTrigger data-testid="mobile-tab-exceptions" value="exceptions">{t("mgmtTimekeeping.tabExceptions")}</TabsTrigger>
            <TabsTrigger data-testid="mobile-tab-history" value="history">{t("mgmtTimekeeping.tabHistory")}</TabsTrigger>
          </TabsList>
          <TabsContent value="source" className="space-y-3 mt-3">
            <div className="h-72"><ScanViewer file={review.blue_file} label="BLUE" /></div>
            <div className="h-72"><ScanViewer file={review.red_file} label="RED" /></div>
          </TabsContent>
          <TabsContent value="attendance" className="mt-3">
            <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
              <table className="w-full text-sm min-w-[560px]">
                <TableHeader />
                <tbody>
                  {review.daily_records.map((row) => (
                    <DailyRecordRow key={row.date} row={row} review={review} isLocked={isLocked} onCorrect={setCorrectingRow} />
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
          <TabsContent value="exceptions" className="mt-3 space-y-2">
            {review.daily_records.filter((r) => r.classification !== "MATCH").map((row) => (
              <div key={row.date} className="bg-white rounded-xl border border-amber-200 p-3" data-testid={`mobile-exception-${row.date}`}>
                <div className="flex items-center justify-between"><p className="text-sm font-semibold">{row.date}</p><StatusBadge status={row.classification} /></div>
                <p className="text-xs text-gray-500 mt-1">{row.notes || t("mgmtTimekeeping.noAdditionalNotes")}</p>
              </div>
            ))}
            {exceptionCount === 0 && <p className="text-sm text-gray-500 text-center py-6">{t("mgmtTimekeeping.noExceptionsThisMonth")}</p>}
          </TabsContent>
          <TabsContent value="history" className="mt-3 space-y-3">
            {review.daily_records.map((row) => (
              <div key={row.date} className="bg-white rounded-xl border border-gray-200 p-3" data-testid={`mobile-history-${row.date}`}>
                <p className="text-sm font-semibold mb-1">{row.date}</p>
                <LineageIndicator review={review} row={row} compact />
              </div>
            ))}
          </TabsContent>
        </Tabs>
      </div>

      <CorrectionDialog open={!!correctingRow} onOpenChange={(next) => !next && setCorrectingRow(null)} row={correctingRow} onSubmit={submitCorrection} />
      <ReopenMonthDialog open={reopenOpen} onOpenChange={setReopenOpen} review={review} onSubmit={reopenMonth} />
    </div>
  );
}
